import RenderHTML from '../../../../../../../../components/ui-components/RenderHTML'
import { showAsFormattedRupeeAmount, formatRupeeLabel } from '../../../../../display-values/lumpsumPlanAmount'
import { useBottomSheet } from '../../../../../../../../state-management/contexts/BottomSheetContext'
import { lightImpactVibrate } from '../../../../../../../../utils/appBridge'
import type { LumpsumRebalanceSellRecommendation } from '../../../../api/rebalance.types'
import { PlanCategoryBucketKey, PlansPlanMode } from '../../../../api/types'
import { useOpenPlanRecommendationBottomSheet } from '../../../../plan-bottom-sheet/open/useOpenPlanRecommendationBottomSheet'
import { usePlanSellAction } from '../../../../../execution/sell/usePlanSellAction'
import { useLumpsumPlansContext } from '../../../LumpsumPlansProvider'
import { isPlanRolledOut } from '../../../timer/shared/planStatus'
import {
  PlanFundInsightVisual,
  PlansFundCard,
  type PlanFundCardCta,
  type PlanFundCardInsight
} from '../../../lumpsum-ui-components/PlansFundCard'
import {
  ALLOCATION_SELL_FUND_CARD_FULL_SELL_ICON_SVG,
  ALLOCATION_SELL_FUND_CARD_PARTIAL_SELL_ICON_SVG
} from '../../allocation/cards/AllocationSellFundCard'
import { RebalanceHighExposureAllocationUpsellBottomSheet } from './RebalanceHighExposureAllocationUpsellBottomSheet'
import { isPlanFullSell } from '../../../../../utils/planExecutionUtils'
import { PlansFundRole, PlansScreen } from '../../../../../analytics/plansEvents'
import { trackPlansFundCardCta, trackPlansFundCardOpen } from '../../../../../analytics/plansTrack'

type RebalanceSellFundCardProps = {
  recommendation: LumpsumRebalanceSellRecommendation
  showBottomDivider?: boolean
}

export function RebalanceSellFundCard({
  recommendation,
  showBottomDivider = true
}: RebalanceSellFundCardProps) {
  const { openPlanRecommendationBottomSheet } = useOpenPlanRecommendationBottomSheet()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const { allocationPlansData, handlePlanModeChange, rebalancePlansData } = useLumpsumPlansContext()
  const { onSell } = usePlanSellAction({ transactionCta: rebalancePlansData?.transactionCta })
  const isin = recommendation?.isin ?? ''
  const fundName = recommendation?.fundName ?? ''
  const screenName = PlansScreen.LumpsumRebalance
  const fundRole = PlansFundRole.Sell
  const isHighExposure = recommendation?.highExposure ?? false
  const sellRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: recommendation?.sellAmount ?? 0 })
  const hasAllocationPlan =
    isPlanRolledOut(allocationPlansData?.planStatus) &&
    allocationPlansData?.isPlanSubscriptionEligible === true

  const openRebalanceRecommendationBottomSheet = () => {
    openPlanRecommendationBottomSheet({
      isin,
      bucketType: PlanCategoryBucketKey.sellRecommendations
    })
  }

  const openPlanBottomSheet = () => {
    if (isHighExposure) {
      lightImpactVibrate()
      openBottomSheet(
        <RebalanceHighExposureAllocationUpsellBottomSheet
          hasAllocationPlan={hasAllocationPlan}
          onDismiss={closeBottomSheet}
          onViewAllocationPlan={() => {
            closeBottomSheet()
            handlePlanModeChange(PlansPlanMode.ALLOCATION)
          }}
          onViewRebalancePlan={() => {
            closeBottomSheet()
            openRebalanceRecommendationBottomSheet()
          }}
        />
      )
      return
    }

    openRebalanceRecommendationBottomSheet()
  }

  const handleCardClick = () => {
    trackPlansFundCardOpen({ screenName, fundRole, fundName, fundIsin: isin })
    openPlanBottomSheet()
  }

  const ctas: PlanFundCardCta[] = [
    {
      id: `${isin}-action`,
      label: 'Sell',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Sell', fundName, fundIsin: isin })
        onSell({
          isin,
          fundName,
          amount: sellRecommendationFormattedAmount.value,
          currentValue: recommendation?.currentValue ?? 0
        })
      },
      variant: 'tertiary'
    },
    {
      id: `${isin}-view-plan`,
      label: 'View plan',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'View plan', fundName, fundIsin: isin })
        openPlanBottomSheet()
      },
      variant: 'tertiary'
    }
  ]

  return (
    <PlansFundCard
      fund={{
        isin,
        logoUrl: recommendation?.mfLogoUrl ?? '',
        title: fundName,
        fundNature: recommendation?.fundNature ?? '',
        category: recommendation?.fundCategory ?? '',
        purchaseMode: recommendation?.purchaseMode ?? ''
      }}
      topRight={<RebalanceSellFundCardTrailing recommendation={recommendation} />}
      status={{
        rank: recommendation?.powerRank ?? 0,
        rating: recommendation?.powerRating ?? ''
      }}
      recommendationStatus={recommendation?.recommendationStatus}
      recommendationStatusDisplayText={recommendation?.recommendationStatusDisplayText}
      ctas={ctas}
      insights={buildRebalanceSellInsights(recommendation)}
      showBottomDivider={showBottomDivider}
      onCardClick={handleCardClick}
    />
  )
}

export function buildRebalanceSellInsights(
  recommendation: LumpsumRebalanceSellRecommendation
): PlanFundCardInsight[] {
  const isin = recommendation?.isin ?? ''
  const insights: PlanFundCardInsight[] = []
  const missedGains = recommendation?.missedGains ?? 0

  if (missedGains > 0) {
    insights.push({
      id: `${isin}-missed-gains`,
      text: `${formatRupeeLabel({ rawAmount: missedGains })} missed gains`,
      visual: PlanFundInsightVisual.REBALANCE
    })
  }
  if (recommendation?.underPerformance) {
    insights.push({
      id: `${isin}-rebalance-under-performance`,
      text: 'Poor performance',
      visual: PlanFundInsightVisual.REBALANCE
    })
  }
  if (recommendation?.highExposure) {
    insights.push({
      id: `${isin}-rebalance-high-exposure`,
      text: 'High exposure (concentration risk)',
      visual: PlanFundInsightVisual.ALLOCATION
    })
  }

  return insights
}

export function RebalanceSellFundCardTrailing({
  recommendation
}: {
  recommendation: LumpsumRebalanceSellRecommendation
}) {
  const sellAmount = recommendation?.sellAmount ?? 0
  const sellRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: sellAmount })
  const isHighExposure = recommendation?.highExposure ?? false

  if (isHighExposure) {
    return (
      <div className="shrink-0 text-[14px] font-[500] leading-[150%] text-[#232323] whitespace-nowrap">
        ₹*****
      </div>
    )
  }

  const fundMovement = resolveRebalanceSellFundMovement(
    sellAmount,
    recommendation?.currentValue ?? 0
  )

  return (
    <div className="flex shrink-0 flex-col items-end gap-0">
      <div className="text-[14px] font-[500] leading-[150%] text-[#232323] whitespace-nowrap">
        Sell {sellRecommendationFormattedAmount.label}
      </div>
      <div className="flex items-center justify-end gap-1 py-1 pl-1.5">
        <RenderHTML
          innerHTMLText={fundMovement.iconHtml}
          className={`size-3 shrink-0 overflow-hidden ${fundMovement.iconClassName ?? ''}`}
        />
        <div className="text-[13px] font-[400] leading-[140%] text-[#3D3D3D] whitespace-nowrap">
          {fundMovement.text}
        </div>
      </div>
    </div>
  )
}

export function resolveRebalanceSellFundMovement(
  sellAmount: number,
  currentValue: number
): { iconHtml: string; iconClassName?: string; text: string } {
  if (isPlanFullSell(sellAmount, currentValue)) {
    return {
      iconHtml: ALLOCATION_SELL_FUND_CARD_FULL_SELL_ICON_SVG,
      iconClassName: 'rotate-180',
      text: 'Full Sell'
    }
  }

  return {
    iconHtml: ALLOCATION_SELL_FUND_CARD_PARTIAL_SELL_ICON_SVG,
    text: 'Partial Sell'
  }
}
